import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Form, Input, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useAppSelector } from '../store';
import { normalizeFileUrl } from '../services/fileService';
import { userService, type ProviderIncomeFlowTrendItem, type UserProviderSettlementFlow } from '../services/userService';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const formatMoney = (value?: number) => `¥${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const ProviderSettlementFlows: React.FC = () => {
  const [form] = Form.useForm();
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UserProviderSettlementFlow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [trendData, setTrendData] = useState<ProviderIncomeFlowTrendItem[]>([]);

  const buildParams = () => {
    const values = form.getFieldsValue();
    const dateRange = values.dateRange as [Dayjs, Dayjs] | undefined;
    return {
      providerName: user?.isProvider ? undefined : values.providerName,
      providerPhone: user?.isProvider ? undefined : values.providerPhone,
      startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
  };

  const loadTrend = async () => {
    const res = await userService.listProviderSettlementFlowTrend(buildParams());
    setTrendData(res || []);
  };

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await userService.listAllProviderSettlementFlows({
        ...buildParams(),
        page: nextPage,
        pageSize: nextPageSize,
      });
      setData(res.list || []);
      setTotal(res.total || 0);
      setPage(res.page || nextPage);
      setPageSize(res.pageSize || nextPageSize);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void Promise.all([loadData(1, pageSize), loadTrend()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trendOption = useMemo(() => {
    const dates = trendData.map(item => item.date);
    const amounts = trendData.map(item => Number(item.amount || 0));
    const counts = trendData.map(item => Number(item.count || 0));
    return {
      color: ['#1677ff', '#13c2c2'],
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          const amountItem = params?.[0];
          const countItem = params?.[1];
          return [
            amountItem?.axisValue || '',
            `结算金额：${formatMoney(amountItem?.data || 0)}`,
            `流水笔数：${Number(countItem?.data || 0)} 笔`,
          ].join('<br/>');
        },
      },
      legend: {
        top: 0,
        data: ['结算金额', '流水笔数'],
      },
      grid: {
        left: 16,
        right: 24,
        top: 44,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLine: { lineStyle: { color: '#d9d9d9' } },
        axisLabel: { color: '#6b7280' },
      },
      yAxis: [
        {
          type: 'value',
          name: '结算金额',
          axisLabel: { formatter: (value: number) => `¥${value}`, color: '#6b7280' },
          splitLine: { lineStyle: { color: '#f0f0f0' } },
        },
        {
          type: 'value',
          name: '笔数',
          axisLabel: { formatter: (value: number) => `${value}`, color: '#6b7280' },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '结算金额',
          type: 'line',
          smooth: true,
          areaStyle: { opacity: 0.12 },
          symbolSize: 6,
          data: amounts,
        },
        {
          name: '流水笔数',
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          symbolSize: 6,
          data: counts,
        },
      ],
    };
  }, [trendData]);

  const columns: ColumnsType<UserProviderSettlementFlow> = [
    { title: '代理商用户名', dataIndex: 'providerUsername', key: 'providerUsername', width: 140 },
    { title: '代理商姓名', dataIndex: 'providerName', key: 'providerName', width: 140 },
    { title: '代理商电话', dataIndex: 'providerPhone', key: 'providerPhone', width: 140 },
    { title: '结算金额', dataIndex: 'amount', key: 'amount', width: 120, render: formatMoney },
    { title: '结算前', dataIndex: 'beforeAmount', key: 'beforeAmount', width: 120, render: formatMoney },
    { title: '结算后', dataIndex: 'afterAmount', key: 'afterAmount', width: 120, render: formatMoney },
    { title: '操作人员', dataIndex: 'operatorName', key: 'operatorName', width: 140, render: value => value || '-' },
    { title: '结算备注', dataIndex: 'remark', key: 'remark', ellipsis: true, render: value => value || '-' },
    {
      title: '结算发票',
      dataIndex: 'invoiceUrl',
      key: 'invoiceUrl',
      width: 110,
      render: (url: string) => url ? <a href={normalizeFileUrl(url)} target="_blank" rel="noreferrer">查看</a> : '-',
    },
    { title: '结算时间', dataIndex: 'createdAt', key: 'createdAt', width: 180, render: formatDateTime },
  ];

  return (
    <div>
      <Title level={2}>代理商结算流水</Title>
      <Card>
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          {!user?.isProvider ? (
            <>
              <Form.Item name="providerName" label="代理商">
                <Input allowClear placeholder="用户名/姓名" style={{ width: 180 }} />
              </Form.Item>
              <Form.Item name="providerPhone" label="电话">
                <Input allowClear placeholder="代理商电话" style={{ width: 160 }} />
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="dateRange" label="结算日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={() => void Promise.all([loadData(1, pageSize), loadTrend()])}>查询</Button>
              <Button onClick={() => {
                form.resetFields();
                void Promise.all([loadData(1, pageSize), loadTrend()]);
              }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        <Card
          size="small"
          title="结算流水趋势"
          style={{ marginBottom: 16 }}
          styles={{ body: { padding: '16px 16px 8px' } }}
        >
          <ReactECharts option={trendOption} style={{ height: 280 }} />
        </Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 1300 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: value => `共 ${value} 条`,
            onChange: (nextPage, nextPageSize) => void loadData(nextPage, nextPageSize),
          }}
        />
      </Card>
    </div>
  );
};

export default ProviderSettlementFlows;
